import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useSettings } from '../../hooks'
import './Tooltip.css'

/**
 * Tooltip Component
 *
 * Displays simple or advanced tooltips based on user preference.
 *
 * Usage:
 * <Tooltip simple="Brief description" advanced="Detailed technical info">
 *   <button>Hover me</button>
 * </Tooltip>
 */
function Tooltip({ children, simple = '', advanced = '', placement = 'top', delay = 300 }) {
  const { preferences } = useSettings()
  const tooltipLevel = preferences?.tooltipLevel || 'simple'

  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const timeoutRef = useRef(null)
  const targetRef = useRef(null)
  const tooltipRef = useRef(null)

  // If tooltips are off or no content provided, just render children
  if (tooltipLevel === 'off' || (!simple && !advanced)) {
    return <>{children}</>
  }

  // Determine which content to show
  const content = tooltipLevel === 'advanced' ? (advanced || simple) : simple

  if (!content) {
    return <>{children}</>
  }

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true)
      calculatePosition()
    }, delay)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsVisible(false)
  }

  const calculatePosition = () => {
    if (!targetRef.current) return

    const targetRect = targetRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current?.getBoundingClientRect() || { width: 200, height: 40 }

    let top = 0
    let left = 0

    switch (placement) {
      case 'top':
        top = targetRect.top - tooltipRect.height - 8
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2)
        break
      case 'bottom':
        top = targetRect.bottom + 8
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2)
        break
      case 'left':
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2)
        left = targetRect.left - tooltipRect.width - 8
        break
      case 'right':
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2)
        left = targetRect.right + 8
        break
      default:
        top = targetRect.top - tooltipRect.height - 8
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2)
    }

    // Keep tooltip within viewport
    const margin = 8
    if (left < margin) left = margin
    if (left + tooltipRect.width > window.innerWidth - margin) {
      left = window.innerWidth - tooltipRect.width - margin
    }
    if (top < margin) top = targetRect.bottom + 8 // Flip to bottom if no room on top

    setPosition({ top, left })
  }

  // Recalculate position on scroll/resize
  useEffect(() => {
    if (isVisible) {
      calculatePosition()
      window.addEventListener('scroll', calculatePosition, true)
      window.addEventListener('resize', calculatePosition)

      return () => {
        window.removeEventListener('scroll', calculatePosition, true)
        window.removeEventListener('resize', calculatePosition)
      }
    }
  }, [isVisible])

  return (
    <>
      <span
        ref={targetRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="tooltip-trigger"
      >
        {children}
      </span>

      {isVisible && createPortal(
        <div
          ref={tooltipRef}
          className={`tooltip tooltip-${placement} tooltip-${tooltipLevel}`}
          style={{ top: `${position.top}px`, left: `${position.left}px` }}
        >
          <div className="tooltip-content">{content}</div>
          <div className={`tooltip-arrow tooltip-arrow-${placement}`} />
        </div>,
        document.body
      )}
    </>
  )
}

export default Tooltip
